import axios from "axios";
import Constants from "../../../Variables/Constants";
import { toast } from "react-toastify";
import { setCartItems } from "../../../../redux/cartSlice";

const handleApiError = (error) => {
  toast.clearWaitingQueue();
  toast.dismiss();

  if (error?.response) {
    const { status, message } = error.response.data || {};

    toast.error(message || "Request failed");

    if (status === 401 && message === "Unauthorized!") {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
  } else {
    toast.error(error?.message || "Network error");
  }
};

export const fetchDesigns = async ({
  categoryId,
  collectionId = null,
  setDesignLoading,
  setAllDesign,
}) => {
  setDesignLoading(true);
  const params = { categoryId, limit: 1000 };
  if (collectionId) {
    params.collectionId = collectionId;
  }

  try {
    const resp = await axios.get(Constants.getUrls.getAllDesigns, { params });
    if (resp.data.status === "success") {
      const data = resp.data.data;
      if (data && Array.isArray(data) && data.length > 0) {
        setAllDesign(data);
      }
    }
  } catch (error) {
    console.error("Error in stylesChange:", error);
  } finally {
    setDesignLoading(false);
  }
};

export const getCategoryDetail = async ({
  categoryId,
  setLoader,
  setCategoryData,
}) => {
  setLoader(true);

  try {
    const { data } = await axios.get(
      `${Constants.getUrls.getAllCategory}/${categoryId}`
    );

    if (data?.status !== "success") {
      toast.error(data?.message || "Something went wrong");
      return;
    }

    setCategoryData(data?.data);
  } catch (error) {
    toast.clearWaitingQueue();
    toast.dismiss();
    if (error.response) {
      const { status, message } = error.response.data || {};

      toast.error(message || "Request failed");

      if (status === 401 && message === "Unauthorized!") {
        localStorage.removeItem("token");
        window.location.href = "/";
      }
    } else {
      toast.error(error.message || "Network error");
    }
  } finally {
    setLoader(false);
  }
};

export const getCollectionList = async ({ setLoader, setCollectionList }) => {
  setLoader(true);

  try {
    const { data } = await axios.get(Constants.getUrls.getAllCollections);

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load collections");
      return;
    }

    const docs = data?.data?.docs || [];
    if(docs.length>0){
      docs.push({
        _id: "all",
        title: "All Categories",
        category: docs?.[0]?.category ?? null,
      })
    }

    setCollectionList(docs);
  } catch (error) {
    handleApiError(error);
  } finally {
    setLoader(false);
  }
};

export const getCollectionDetail = async ({
  collectionId,
  setLoader,
  setCollectionData,
}) => {
  setLoader(true);

  try {
    const { data } = await axios.get(
      `${Constants.getUrls.getAllCollections}/${collectionId}`
    );

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load collection");
      return;
    }
    setCollectionData(data?.data);
    return data?.data
  } catch (error) {
    handleApiError(error);
  } finally {
    setLoader(false);
  }
};

export const getCustomization = async ({
  customizeImageId,
  setLoader,
  setDesignData,
}) => {
  try {
    setLoader(true);
    const result = await axios.get(
      Constants.getUrls.getCustomizedImage + `/${customizeImageId}`
    );
    if (result.data.status === "success") {
      setDesignData(result.data.data);
    }
    setLoader(false);
  } catch (error) {
    setLoader(false);
    console.log(error);
  }
};

export const handleSizeData = async ({
  sizeIds,
  setLoader,
  setSizeData,
  setSelectedSize,
  fabricData,
  categoryId = null,
}) => {
  setLoader(true);
  try {
    let url = Constants.postUrls.getMultipleSize;
    
    // Add category_id to query if provided
    if (categoryId) {
      url += `?category_id=${categoryId}`;
    }
    
    const { data } = await axios.post(url, sizeIds);

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load sizes");
      return;
    }

    const sizes = data?.data || [];

    setSizeData(sizes);

    if (sizes.length > 0) {
      const firstSize = sizes[0];

      const sizeDetail = fabricData?.size_details?.find(
        (item) => item?.size_id === firstSize?._id
      );
      
      const price = sizeDetail?.price ?? null;
      const accentPrice = sizeDetail?.accent_price || 0;

      setSelectedSize({
        ...firstSize,
        price,
        accent_price: accentPrice,
      });
    }
  } catch (error) {
    handleApiError(error); // reusable handler from earlier
  } finally {
    setLoader(false);
  }
};

export const getFabricData = async ({
  mainFabricId,
  setLoader,
  setFabricData,
}) => {
  setLoader(true);

  try {
    const { data } = await axios.get(
      `${Constants.getUrls.getSingleFabric}/${mainFabricId}`,
      {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      }
    );

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load fabric");
      return;
    }

    setFabricData(data?.data ?? null);
  } catch (error) {
    handleApiError(error); // reusable error handler
  } finally {
    setLoader(false);
  }
};

export const getSingleProduct = async ({
  id,
  setLoader,
  setCartBtnMsg,
  setProduct,
}) => {
  setLoader(true);

  try {
    const { data } = await axios.get(`${Constants.getUrls.getProduct}/${id}`);

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load product");
      return;
    }

    const product = data?.data;

    if (!product) return;

    if (product?.cart_item) {
      setCartBtnMsg("Go To Cart");
    }

    setProduct(product);
  } catch (error) {
    handleApiError(error);
  } finally {
    setLoader(false);
  }
};

export const fetchCart = async ({ setLoader, dispatch }) => {
  setLoader(true);

  try {
    const { data } = await axios.get(Constants.getUrls.getCart, {
      headers: {
        Authorization: localStorage.getItem("token"),
      },
    });

    if (data?.status !== "success") {
      dispatch(setCartItems([]));
      localStorage.removeItem("cart");
      return;
    }

    const docs = data?.data?.docs ?? [];

    const cartItems = docs.map((item) => ({
      _id: item?._id,
      product_id: item?.product_id?._id,
      customized_image_id: item?.customized_image_id?._id || item?.customized_image_id || null,
      images: item?.product_id?.images,
      name: item?.product_id?.name,
      price: item?.product_id?.price,
      quantity: item?.quantity,
    }));

    dispatch(setCartItems(cartItems));
    localStorage.removeItem("cart");
  } catch (error) {
    handleApiError(error); // shared error handler
  } finally {
    setLoader(false);
  }
};

export const addToCart = async ({ formData, setLoader, dispatch }) => {
  let newProduct = null;
  try {
    const { data: productRes } = await axios.post(
      Constants.postUrls.addProducts,
      formData,
      { headers: { Authorization: localStorage.getItem("token") } }
    );

    if (productRes?.status !== "success") {
      throw new Error(productRes?.message || "Failed to create product");
    }
    newProduct = productRes.data;
    const customizedImageId = formData.get("customize_image_id") || null;
    const productCustomizedImageId =
      newProduct?.customize_image_id?._id || newProduct?.customize_image_id || null;

    if (customizedImageId && String(productCustomizedImageId) !== String(customizedImageId)) {
      throw new Error("Product was created without the customized image reference.");
    }

    // 2️⃣ Add to cart
    const { data: cartRes } = await axios.post(
      Constants.postUrls.AddToCart,
      {
        product_id: newProduct._id,
        quantity: 1,
        customized_image_id: customizedImageId,
      },
      { headers: { Authorization: localStorage.getItem("token") } }
    );

    if (cartRes?.status === "success") {
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.success("Product added to cart successfully!", {
        style: { backgroundColor: "green", color: "white" },
      });

      fetchCart({ setLoader, dispatch });
    }

    if (cartRes?.status !== "success") {
      throw new Error(cartRes?.message || "Failed to add product to cart");
    }

    return cartRes;
  } catch (error) {
    if (newProduct?._id) {
      try {
        await axios.delete(`${Constants.deleteUrls.removeProduct}/${newProduct._id}`, {
          headers: { Authorization: localStorage.getItem("token") },
        });
      } catch (cleanupError) {
        console.error("Failed to clean up product after cart error:", cleanupError);
      }
    }
    handleApiError(error);
    throw error;
  } finally {
    setLoader(false);
  }
};

export const fetchAddOns = async ({ setAddOns, categoryId = null }) => {
  setAddOns((prev) => ({
    ...prev,
    loading: true,
    error: null,
  }));

  try {
    let url = Constants.getUrls.getAddOns;
    
    // Add category_id to query if provided
    if (categoryId) {
      url += `?category_id=${categoryId}`;
    }
    
    const { data } = await axios.get(url);

    if (data?.status !== "success") {
      throw new Error(data?.message || "Failed to fetch add-ons");
    }

    setAddOns({
      loading: false,
      error: null,
      data,
    });
  } catch (error) {
    setAddOns({
      loading: false,
      error:
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong",
      data: null,
    });
  }
};

export const fetchPersonalizeColorList = async ({ setColorList, categoryId = null }) => {
  try {
    let url = `${Constants.getUrls.getAllThread}?limit=100&page=1`;
    
    // Add category_id to query if provided
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    
    const response = await axios.get(url);
    const resData = response.data;
    if (resData && resData.status === "success") {
      setColorList(resData.data.docs || []);
    } else {
      setColorList([]);
    }
  } catch (err) {
    console.error("getDesigns error:", err);
  }
};

export const getAllFabric = async ({
  setMainFabricList,
  setActiveTabState = [],
  setSelectedTabData = [],
  categoryId = null,
}) => {
  try {
    let url = `${Constants.getUrls.getAllFabric}?limit=100`;
    
    // Add category_id to query if provided
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    
    const { data } = await axios.get(url);

    if (data?.status !== "success") {
      toast.error(data?.message || "Failed to load fabrics");
      return;
    }

    const groupedFabrics = data?.groupedFabrics;
    if (!groupedFabrics || Object.keys(groupedFabrics).length === 0) return;

    // Set the main fabric list
    setMainFabricList(groupedFabrics);
    
    // Get the first tab name
    const firstTab = Object.keys(groupedFabrics)[0];
    
    // Set active tab state for both main and secondary
    setActiveTabState?.forEach((set) => set(firstTab));
    
    // Set selected tab data for both main and secondary
    setSelectedTabData?.forEach((set) => set(groupedFabrics[firstTab]));
    
    console.log("Fabric list loaded:", {
      firstTab,
      fabricCount: groupedFabrics[firstTab]?.length,
      allTabs: Object.keys(groupedFabrics)
    });
  } catch (error) {
    handleApiError(error); // shared error handler
  }
};

